import { useState } from 'react';
import {
  useExtensionTypes, useFeatures, useSaveExtensionType, useDeleteExtensionType,
} from '../../lib/api/hooks-products.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Switch } from '../../components/ui/Form.js';
import { formatMoney } from '../../lib/format.js';
import { useSession } from '../../lib/auth/session.js';
import type { ExtensionType } from '@arcnet/contracts';

/**
 * Extension types are PRODUCTS: a feature set with a price.
 *
 * Both the invoice line and the switch's entitlement check read the same
 * definition, so what a customer is charged for and what they can actually do
 * cannot drift apart.
 */
export function ExtensionTypesPage() {
  const can = useSession((s) => s.can);
  const types = useExtensionTypes();
  const del = useDeleteExtensionType();
  const [editing, setEditing] = useState<ExtensionType | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Extension types</h1>
          <p className="arc-muted" style={{ marginTop: 3 }}>
            The seat products you sell. Each carries a feature set and a monthly price.
          </p>
        </div>
        {can('product:write') && (
          <button type="button" className="arc-btn arc-btn-primary"
            onClick={() => { setCreating((v) => !v); setEditing(null); }}>
            {creating ? 'Cancel' : '+ New type'}
          </button>
        )}
      </div>

      {types.error && <ErrorBanner error={types.error} />}

      {(creating || editing) && (
        <TypeEditor
          existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }}
        />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Type</th><th>Features</th>
                <th className="arc-td-right">Monthly</th>
                <th className="arc-td-right">Setup</th>
                <th className="arc-td-right">Feature cost</th>
                <th className="arc-td-right">In use</th>
                <th>Status</th><th />
              </tr>
            </thead>
            {types.data === undefined ? <TableSkeleton cols={8} /> : (
              <tbody>
                {types.data?.data.map((t) => {
                  const margin = Number(t.monthly_price) - Number(t.feature_cost ?? '0');
                  return (
                    <tr key={t.id}>
                      <td>
                        <div className="arc-stack">
                          <span style={{ fontWeight: 570 }}>{t.name}</span>
                          <span className="arc-subtle arc-mono" style={{ fontSize: 11.5 }}>{t.key}</span>
                        </div>
                      </td>
                      <td style={{ maxWidth: 340 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {t.features.slice(0, 5).map((f) => (
                            <Badge key={f} tone="muted">{f.replace(/_/g, ' ')}</Badge>
                          ))}
                          {t.features.length > 5 && (
                            <Badge tone="info">+{t.features.length - 5}</Badge>
                          )}
                        </div>
                      </td>
                      <td className="arc-td-right arc-num" style={{ fontWeight: 600 }}>
                        {formatMoney(t.monthly_price, t.currency)}
                      </td>
                      <td className="arc-td-right arc-num">{formatMoney(t.setup_price, t.currency)}</td>
                      <td className="arc-td-right arc-num arc-subtle">
                        {formatMoney(t.feature_cost ?? '0', t.currency)}
                        {/* Per-seat margin floor: what we pay for the features
                            in this type before any call revenue. */}
                        <div style={{ fontSize: 11, color: margin > 0 ? 'var(--arc-ok)' : 'var(--arc-danger)' }}>
                          {margin >= 0 ? '+' : ''}{formatMoney(String(margin), t.currency)} margin
                        </div>
                      </td>
                      <td className="arc-td-right arc-num">{t.in_use_count ?? 0}</td>
                      <td>
                        <Badge tone={t.is_active ? 'ok' : 'muted'}>
                          {t.is_active ? 'active' : 'inactive'}
                        </Badge>
                      </td>
                      <td className="arc-td-right">
                        {can('product:write') && (
                          <div className="arc-row" style={{ justifyContent: 'flex-end', gap: 4 }}>
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => { setEditing(t); setCreating(false); }}>Edit</button>
                            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                              onClick={() => {
                                if (confirm(`Delete '${t.name}'?`)) del.mutate(t.id);
                              }}>Delete</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>
        </div>
        {types.data !== undefined && types.data?.data.length === 0 && (
          <Empty icon="extension" title="No extension types" hint="Define the seat products you sell." />
        )}
      </div>
    </>
  );
}

function TypeEditor({ existing, onDone }: { existing: ExtensionType | null; onDone: () => void }) {
  const features = useFeatures();
  const save = useSaveExtensionType();

  const [key, setKey] = useState(existing?.key ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [price, setPrice] = useState(existing ? String(Number(existing.monthly_price)) : '0');
  const [setup, setSetup] = useState(existing ? String(Number(existing.setup_price)) : '0');
  const [selected, setSelected] = useState<Set<string>>(new Set(existing?.features ?? []));

  const toggle = (k: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });

  type FeatureRow = { key: string; name: string; description: string; category: string; unit_cost: string };
  const byCategory = (features.data?.data ?? []).reduce<Record<string, FeatureRow[]>>(
    (acc, f) => { (acc[f.category] ??= []).push(f); return acc; }, {},
  );

  const featureCost = (features.data?.data ?? [])
    .filter((f) => selected.has(f.key))
    .reduce((sum, f) => sum + Number(f.unit_cost), 0);

  return (
    <form
      className="arc-card arc-card-body" style={{ marginBottom: 16 }}
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(
          {
            ...(existing ? { id: existing.id } : {}),
            key, name, monthly_price: price, setup_price: setup,
            features: [...selected],
          },
          { onSuccess: onDone },
        );
      }}
    >
      <h2 className="arc-h2" style={{ marginBottom: 14 }}>
        {existing ? `Edit ${existing.name}` : 'New extension type'}
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 140px 140px', gap: 12, marginBottom: 16 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="et-name">Name</label>
          <input id="et-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Enterprise" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="et-key">Key</label>
          <input id="et-key" className="arc-input" required value={key}
            onChange={(e) => setKey(e.target.value)} disabled={!!existing}
            placeholder="enterprise" />
          <div className="arc-hint">
            {existing ? 'Permanent once created' : 'lowercase, no spaces'}
          </div>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="et-price">Monthly</label>
          <input id="et-price" className="arc-input" inputMode="decimal" value={price}
            onChange={(e) => setPrice(e.target.value)} />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="et-setup">Setup</label>
          <input id="et-setup" className="arc-input" inputMode="decimal" value={setup}
            onChange={(e) => setSetup(e.target.value)} />
        </div>
      </div>

      <div className="arc-label" style={{ marginBottom: 8 }}>Feature set</div>
      <div className="arc-grid arc-grid-2" style={{ marginBottom: 14 }}>
        {Object.entries(byCategory).map(([category, list]) => (
          <div key={category} className="arc-card" style={{ boxShadow: 'none' }}>
            <div className="arc-card-head" style={{ padding: '9px 12px' }}>
              <span className="arc-stat-label">{category.replace(/_/g, ' ')}</span>
            </div>
            <div style={{ padding: '8px 12px' }}>
              {list.map((f) => (
                <label key={f.key} className="arc-row"
                  style={{ padding: '7px 0', cursor: 'pointer', gap: 11 }}>
                  <Switch checked={selected.has(f.key)} label={f.name}
                    onChange={() => toggle(f.key)} />
                  <span className="arc-stack" style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13 }}>{f.name}</span>
                    <span className="arc-subtle" style={{ fontSize: 11.5 }}>{f.description}</span>
                  </span>
                  {Number(f.unit_cost) > 0 && (
                    <span className="arc-subtle arc-num" style={{ fontSize: 11.5 }}>
                      {formatMoney(f.unit_cost, 'ZAR')}/mo cost
                    </span>
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="arc-row" style={{ gap: 16 }}>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create type'}
        </button>
        <span className="arc-subtle" style={{ fontSize: 12.5 }}>
          {selected.size} feature{selected.size === 1 ? '' : 's'} ·
          {' '}cost {formatMoney(String(featureCost), 'ZAR')}/seat ·
          {' '}margin {formatMoney(String(Number(price) - featureCost), 'ZAR')}
        </span>
      </div>
    </form>
  );
}
